import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save, Building2, Users, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const SEGMENTOS = ['CAIXA', 'CHAPA', 'CORTE VINCO', 'SIMPLEX'];
const IPI_POR_SEGMENTO = { CAIXA: 15, CHAPA: 3.25, 'CORTE VINCO': 15, SIMPLEX: 3.25 };

const FORM_INITIAL = {
  numero_fe: '', descricao: '', segmento: 'CAIXA',
  peso_unit: '', comissao: '', preco_unit: '',
  ipi: '15',
  representada_id: '', representada_nome: '',
  cliente_id: '', cliente_nome: '',
  comprimento: '', largura: '', altura: '', gramatura: '',
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const toNum = (v) => { if (!v && v !== 0) return 0; return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0; };
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);

const normalizarNome = (valor) => String(valor || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Z0-9]/gi, '')
  .toUpperCase();

const localizarCadastro = (lista, nomeExtraido) => {
  const alvo = normalizarNome(nomeExtraido);
  if (!alvo) return null;
  const exatos = lista.filter(item =>
    [item.nome_fantasia, item.nome].some(nome => normalizarNome(nome) === alvo)
  );
  if (exatos.length === 1) return exatos[0];
  const aproximados = lista.filter(item =>
    [item.nome_fantasia, item.nome].some(nome => {
      const candidato = normalizarNome(nome);
      return candidato && (candidato.includes(alvo) || alvo.includes(candidato));
    })
  );
  return aproximados.length === 1 ? aproximados[0] : null;
};

const SEG_COLORS = {
  CAIXA: 'bg-blue-100 text-blue-700 border-blue-200',
  CHAPA: 'bg-amber-100 text-amber-700 border-amber-200',
  'CORTE VINCO': 'bg-purple-100 text-purple-700 border-purple-200',
  SIMPLEX: 'bg-teal-100 text-teal-700 border-teal-200',
};

const MaterialForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(FORM_INITIAL);
  const [initialData, setInitialData] = useState(FORM_INITIAL);
  const [representadas, setRepresentadas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfExtraido, setPdfExtraido] = useState({});
  const pdfInputRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [rRes, cRes] = await Promise.all([
          api.get('/representadas').catch(() => ({ data: [] })),
          api.get('/clientes').catch(() => ({ data: [] })),
        ]);
        setRepresentadas(Array.isArray(rRes.data) ? rRes.data : []);
        setClientes(Array.isArray(cRes.data) ? cRes.data : []);

        if (isEditing) {
          const r = await api.get(`/materiais/${id}`);
          const m = r.data;
          const seg = m.segmento || 'CAIXA';
          const ipiVal = m.ipi != null ? m.ipi : (IPI_POR_SEGMENTO[seg] ?? 15);
          const data = {
            numero_fe: m.numero_fe || m.codigo || '',
            descricao: m.nome || m.descricao || '',
            segmento: seg,
            peso_unit: String(m.peso_unit || '').replace('.', ','),
            comissao: String(m.comissao || '').replace('.', ','),
            preco_unit: String(m.preco_unit || '').replace('.', ','),
            ipi: String(ipiVal).replace('.', ','),
            representada_id: m.representada_id || '',
            representada_nome: m.representada_nome || '',
            cliente_id: m.cliente_id || '',
            cliente_nome: m.cliente_nome || '',
            comprimento: String(m.comprimento || '').replace('.', ','),
            largura: String(m.largura || '').replace('.', ','),
            altura: String(m.altura || '').replace('.', ','),
            gramatura: String(m.gramatura || '').replace('.', ','),
          };
          setFormData(data);
          setInitialData(data);
        }
      } catch {
        if (isEditing) {
          toast.error('Material não encontrado');
          navigate('/materiais');
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const inpCls = (field) => pdfExtraido[field]
    ? 'w-full border-2 border-emerald-400 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400/10 transition-all'
    : inp;

  const handleBack = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return;
    navigate('/materiais');
  };

  const fatorForm = () => {
    const peso = toNum(formData.peso_unit);
    const preco = toNum(formData.preco_unit);
    return peso > 0 ? fmt(preco / (peso * 1000)) : '0,00';
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const extrairLinhasPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    const linhas = [];

    for (let pagina = 1; pagina <= pdf.numPages; pagina++) {
      const page = await pdf.getPage(pagina);
      const content = await page.getTextContent();
      const grupos = [];

      content.items.filter(item => item.str?.trim()).forEach(item => {
        const x = item.transform?.[4] || 0;
        const y = item.transform?.[5] || 0;
        let grupo = grupos.find(linha => Math.abs(linha.y - y) <= 2);
        if (!grupo) {
          grupo = { y, itens: [] };
          grupos.push(grupo);
        }
        grupo.itens.push({ texto: item.str.trim(), x, largura: item.width || 0 });
      });

      grupos.sort((a, b) => b.y - a.y).forEach(grupo => {
        const itens = grupo.itens.sort((a, b) => a.x - b.x);
        let linha = '';
        let fimAnterior = null;
        itens.forEach(item => {
          const separador = fimAnterior !== null && item.x - fimAnterior > 18 ? '   ' : ' ';
          linha += `${linha ? separador : ''}${item.texto}`;
          fimAnterior = item.x + item.largura;
        });
        linhas.push(linha.trim());
      });
    }

    return linhas;
  };

  const handlePdfSelecionado = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Selecione um arquivo PDF válido.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('O PDF deve ter no máximo 10 MB.');
      return;
    }

    setPdfLoading(true);
    try {
      const linhas = await extrairLinhasPdf(file);
      const texto = linhas.join('\n');
      const obter = (regex) => texto.match(regex)?.[1]?.trim() || '';

      const numeroFe = obter(/Produto:\s*([A-Z0-9./-]+)/i);
      const grupoProduto = obter(/Grupo do Produto:\s*([^\n]+)/i).split(/\s{2,}/)[0].trim().toUpperCase();
      const sufixo = numeroFe.toUpperCase().match(/-([A-Z]+)$/)?.[1] || '';
      const segmento = grupoProduto.startsWith('CHAPA') || sufixo === 'CH'
        ? 'CHAPA'
        : grupoProduto === 'CX' || grupoProduto.startsWith('CAIXA') || sufixo === 'CX'
          ? 'CAIXA'
          : '';

      const indiceCabecalhoCliente = linhas.findIndex(linha =>
        /Clientes Relacionados/i.test(linha) && /Refer.ncia/i.test(linha)
      );
      const linhaClienteReferencia = indiceCabecalhoCliente >= 0
        ? linhas.slice(indiceCabecalhoCliente + 1).find(linha => linha.trim()) || ''
        : '';
      const colunasCliente = linhaClienteReferencia.split(/\s{2,}/).map(valor => valor.trim()).filter(Boolean);
      const clienteExtraido = colunasCliente[0] || '';
      const referencia = colunasCliente[1] || '';

      const comprimentoInterno = obter(/Comprimento:\s*([\d.,]+)\s*mm/i);
      const larguraInterna = obter(/Largura:\s*([\d.,]+)\s*mm/i);
      const altura = obter(/Altura 1:\s*([\d.,]+)\s*mm/i);
      const dimensoesChapa = texto.match(/L X C:\s*([\d.,]+)\s*X\s*([\d.,]+)/i);
      const larguraChapa = dimensoesChapa?.[1] || '';
      const comprimentoChapa = dimensoesChapa?.[2] || '';
      const conteudo = obter(/Conte.do:\s*([^\n]+)/i);
      const gramatura = conteudo.match(/\bG\s*(\d{3,4})\b/i)?.[1] || '';
      const pesoUnitario = obter(/Peso da Unidade:\s*([\d.,]+)\s*Kg/i);
      const comprimento = segmento === 'CHAPA' ? comprimentoChapa : comprimentoInterno;
      const largura = segmento === 'CHAPA' ? larguraChapa : larguraInterna;

      if (!numeroFe || !segmento || !referencia) {
        toast.error('Não foi possível reconhecer FE, segmento e referência neste PDF.');
        return;
      }

      const cliente = localizarCadastro(clientes, clienteExtraido);
      const representada = localizarCadastro(representadas, 'CONPEL');
      const updates = {
        numero_fe: numeroFe.toUpperCase(),
        descricao: referencia.toUpperCase(),
        segmento,
        ipi: String(IPI_POR_SEGMENTO[segmento] ?? 15).replace('.', ','),
        comprimento: comprimento || '',
        largura: largura || '',
        altura: segmento === 'CAIXA' ? (altura || '') : '',
        gramatura: gramatura || '',
        peso_unit: pesoUnitario || '',
        cliente_id: cliente?.id || '',
        cliente_nome: cliente ? (cliente.nome_fantasia || cliente.nome || '') : clienteExtraido,
        representada_id: representada?.id || '',
        representada_nome: representada ? (representada.nome_fantasia || representada.nome || '') : '',
      };
      const extraidos = Object.fromEntries(
        Object.entries(updates).filter(([, valor]) => valor !== '').map(([campo]) => [campo, true])
      );

      setFormData(prev => ({ ...prev, ...updates }));
      setPdfExtraido(extraidos);
      const avisos = [];
      if (clienteExtraido && !cliente) avisos.push(`Cliente "${clienteExtraido}" não localizado`);
      if (!representada) avisos.push('Representada Conpel não localizada');
      toast.success('Dados técnicos preenchidos pelo PDF. Confira antes de salvar.');
      if (avisos.length) toast.warning(avisos.join('. '));
    } catch {
      toast.error('Erro ao ler o PDF do material.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return toast.error('Ação não permitida');
    setSaving(true);
    try {
      const data = {
        numero_fe: formData.numero_fe,
        codigo: formData.numero_fe,
        nome: formData.descricao,
        descricao: formData.descricao,
        segmento: formData.segmento,
        peso_unit: toNum(formData.peso_unit),
        comissao: toNum(formData.comissao),
        preco_unit: toNum(formData.preco_unit),
        ipi: toNum(formData.ipi),
        representada_id: formData.representada_id || '',
        representada_nome: formData.representada_nome || '',
        cliente_id: formData.cliente_id || '',
        cliente_nome: formData.cliente_nome || '',
        comprimento: toNum(formData.comprimento),
        largura: toNum(formData.largura),
        altura: toNum(formData.altura),
        gramatura: toNum(formData.gramatura),
      };
      if (isEditing) {
        await api.put(`/materiais/${id}`, data);
        toast.success('Material atualizado!');
      } else {
        await api.post('/materiais', data);
        toast.success('Material cadastrado!');
      }
      navigate('/materiais');
    } catch {
      toast.error('Erro ao salvar material');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Material...</p>
    </div>
  );

  return (
    <form id="material-form" onSubmit={handleSubmit} className="min-h-screen bg-[#E9EEF2]">

      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={e => { handlePdfSelecionado(e.target.files?.[0]); e.target.value = ''; }}
      />

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
              <ArrowLeft size={14} /> Voltar
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <div>
              <h1 className="text-base font-black text-slate-900">
                {isEditing ? `Editar Material — ${formData.numero_fe || id}` : 'Novo Material'}
              </h1>
              <p className="text-[11px] text-slate-400 font-semibold">Catálogo Base (FE)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                disabled={pdfLoading}
                className="flex items-center gap-2 bg-white border border-[#0A3D73] text-[#0A3D73] hover:bg-blue-50 text-xs font-black uppercase px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <FileText size={14} /> {pdfLoading ? 'Lendo PDF...' : 'Cadastrar com PDF'}
              </button>
            )}
            <button
              type="submit"
              form="material-form"
              disabled={saving || pdfLoading}
              className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Save size={14} /> {saving ? 'Salvando...' : 'Salvar Material'}
            </button>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 max-w-6xl mx-auto">

          {/* ── COL ESQUERDA ── */}
          <div className="space-y-6">

            {/* Card 01 · Identificação */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">01 · Identificação</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Número do FE *</label>
                  <input
                    value={formData.numero_fe}
                    onChange={e => set('numero_fe', e.target.value.toUpperCase())}
                    required className={inpCls('numero_fe')} placeholder="Ex: 1234/26" />
                </div>
                <div>
                  <label className={lbl}>Segmento</label>
                  <select
                    value={formData.segmento}
                    onChange={e => {
                      const seg = e.target.value;
                      setFormData(prev => ({ ...prev, segmento: seg, ipi: String(IPI_POR_SEGMENTO[seg] ?? 15).replace('.', ',') }));
                    }}
                    className={`${inpCls('segmento')} cursor-pointer`}>
                    {SEGMENTOS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Nome do Produto *</label>
                  <input
                    value={formData.descricao}
                    onChange={e => set('descricao', e.target.value.toUpperCase())}
                    required className={inpCls('descricao')} placeholder="Descrição técnica do produto" />
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Representada</label>
                  <select
                    value={formData.representada_id}
                    onChange={e => {
                      const rep = representadas.find(r => r.id === e.target.value);
                      setFormData(prev => ({ ...prev, representada_id: e.target.value, representada_nome: rep ? (rep.nome_fantasia || rep.nome) : '' }));
                    }}
                    className={`${inpCls('representada_id')} cursor-pointer`}>
                    <option value="">— Nenhuma —</option>
                    {representadas.map(r => <option key={r.id} value={r.id}>{r.nome_fantasia || r.nome}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Cliente (dono da FE)</label>
                  <select
                    value={formData.cliente_id}
                    onChange={e => {
                      const cli = clientes.find(c => c.id === e.target.value);
                      setFormData(prev => ({ ...prev, cliente_id: e.target.value, cliente_nome: cli ? (cli.nome_fantasia || cli.nome) : '' }));
                    }}
                    className={`${inpCls('cliente_id')} cursor-pointer`}>
                    <option value="">— Selecionar cliente —</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.nome}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Card 02 · Dimensões (oculto para SIMPLEX) */}
            {formData.segmento !== 'SIMPLEX' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">02 · Dimensões & Qualidade</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Comprimento (mm)</label>
                    <input value={formData.comprimento} onChange={e => set('comprimento', e.target.value)} className={inpCls('comprimento')} placeholder="0" />
                  </div>
                  <div>
                    <label className={lbl}>Largura (mm)</label>
                    <input value={formData.largura} onChange={e => set('largura', e.target.value)} className={inpCls('largura')} placeholder="0" />
                  </div>
                  {(formData.segmento === 'CAIXA' || formData.segmento === 'CORTE VINCO') && (
                    <div>
                      <label className={lbl}>Altura (mm)</label>
                      <input value={formData.altura} onChange={e => set('altura', e.target.value)} className={inpCls('altura')} placeholder="0" />
                    </div>
                  )}
                  <div>
                    <label className={lbl}>Gramatura (g/m²)</label>
                    <input value={formData.gramatura} onChange={e => set('gramatura', e.target.value)} className={inpCls('gramatura')} placeholder="0" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── COL DIREITA ── */}
          <div className="space-y-6">

            {/* Card 03 · Precificação */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">03 · Precificação</p>
              <div className="space-y-4">
                <div>
                  <label className={lbl}>Peso Unitário (KG)</label>
                  <input value={formData.peso_unit} onChange={e => set('peso_unit', e.target.value)} className={inpCls('peso_unit')} placeholder="0,000000" />
                </div>
                <div>
                  <label className={lbl}>R$ Milheiro</label>
                  <input value={formData.preco_unit} onChange={e => set('preco_unit', e.target.value)} className={inp} placeholder="0,00" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>Comissão %</label>
                    <input value={formData.comissao} onChange={e => set('comissao', e.target.value)}
                      className={`${inp} border-emerald-200 focus:border-emerald-500`} placeholder="Ex: 2,5" />
                  </div>
                  <div>
                    <label className={lbl}>IPI %</label>
                    <input value={formData.ipi} onChange={e => set('ipi', e.target.value)}
                      className={`${inp} border-amber-200 focus:border-amber-500`} placeholder="Ex: 15" />
                  </div>
                </div>
                <div>
                  <label className={lbl}>Fator R$/KG</label>
                  <div className="h-10 flex items-center px-3 bg-[#EFF6FF] border border-[#0A3D73]/20 rounded-lg text-sm font-black text-[#0A3D73]">
                    R$ {fatorForm()}
                  </div>
                </div>
              </div>
            </div>

            {/* Card Resumo */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Resumo</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Segmento</span>
                  <span className={`px-3 py-1 text-xs font-black border rounded-full ${SEG_COLORS[formData.segmento] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {formData.segmento || '—'}
                  </span>
                </div>
                <div className="bg-[#EFF6FF] rounded-xl p-4 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A3D73]/60 mb-1">Fator R$/KG</p>
                  <p className="text-3xl font-black text-[#0A3D73]">R$ {fatorForm()}</p>
                </div>
                <div className="flex items-start gap-2.5 pt-1">
                  <Building2 size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Representada</p>
                    <p className="text-sm font-black text-slate-700 truncate">{formData.representada_nome || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Users size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Cliente</p>
                    <p className="text-sm font-black text-slate-700 truncate">{formData.cliente_nome || '—'}</p>
                  </div>
                </div>
                {formData.comprimento ? (
                  <div className="pt-1 border-t border-slate-100">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-0.5">Dimensões</p>
                    <p className="text-sm font-black font-mono text-slate-700">
                      {formData.comprimento}×{formData.largura}
                      {(formData.segmento === 'CAIXA' || formData.segmento === 'CORTE VINCO') && formData.altura ? `×${formData.altura}` : ''}mm
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </div>
      </div>
    </form>
  );
};

export default MaterialForm;
